import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';

const USER_ID_STORAGE_KEY = 'platform_lastKnownUserId';

export interface SessionUserContextType {
  userId: string | null;
  isInitialQueryFinished: boolean;
  updateUserId: (newUserId: string | null) => void;
  setInitialQueryFinished: (finished: boolean) => void;
}

const SessionUserContext = createContext<SessionUserContextType | undefined>(
  undefined,
);

export const SessionUserProvider = ({ children }: { children: ReactNode }) => {
  const [userId, setUserId] = useState<string | null>(() => {
    return localStorage.getItem(USER_ID_STORAGE_KEY);
  });
  const [isInitialQueryFinished, setIsInitialQueryFinished] = useState(false);

  const updateUserId = useCallback((newUserId: string | null) => {
    if (newUserId) {
      localStorage.setItem(USER_ID_STORAGE_KEY, newUserId);
    } else {
      localStorage.removeItem(USER_ID_STORAGE_KEY);
    }
    setUserId(newUserId);
  }, []);

  // Create a stable callback for setInitialQueryFinished
  const setInitialQueryFinishedCallback = useCallback((finished: boolean) => {
    setIsInitialQueryFinished(finished);
  }, []);

  return (
    <SessionUserContext.Provider
      value={{
        userId,
        isInitialQueryFinished,
        updateUserId,
        setInitialQueryFinished: setInitialQueryFinishedCallback,
      }}
    >
      {children}
    </SessionUserContext.Provider>
  );
};

export const useSessionUser = (): SessionUserContextType => {
  const context = useContext(SessionUserContext);
  if (context === undefined) {
    throw new Error('useSessionUser must be used within a SessionUserProvider');
  }
  return context;
};
